import { warrantyStatus, ageInYears } from '@servwell/shared';
import { StatusBadge } from '../ui/Badge';

/** Today as YYYY-MM-DD in the browser's own zone — a warranty is a calendar fact. */
export function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * "Under warranty" (PRD v2 §6.7) — the badge that decides who eats the call, so it says
 * WHICH cover and until when rather than a bare green pill. A shop that reads "covered"
 * and finds out it was only the compressor has been misled by its own software.
 *
 * Cover that lapsed recently gets its own amber state: "ran out in June" is the sentence
 * that turns a surprise invoice into an understood one.
 */
export function WarrantyBadge({
  partsExpiresOn,
  laborExpiresOn,
  today = todayIso(),
}: {
  partsExpiresOn: string | null;
  laborExpiresOn: string | null;
  today?: string;
}) {
  const s = warrantyStatus(today, partsExpiresOn, laborExpiresOn);

  if (s.covered) {
    const what =
      s.kind === 'both' ? 'Parts + labor' : s.kind === 'parts' ? 'Parts' : 'Labor';
    // Inside a month, the countdown is the actionable half — quote the replacement now.
    const when =
      s.daysLeft !== null && s.daysLeft <= 31
        ? `${s.daysLeft} day${s.daysLeft === 1 ? '' : 's'} left`
        : `to ${s.expiresOn}`;
    return (
      <StatusBadge tone={s.daysLeft !== null && s.daysLeft <= 31 ? 'warn' : 'success'}>
        {what} warranty · {when}
      </StatusBadge>
    );
  }

  if (s.recentlyExpired) {
    return <StatusBadge tone="warn">Warranty ran out {s.expiresOn}</StatusBadge>;
  }
  if (s.expiresOn) {
    return <StatusBadge tone="neutral">Out of warranty</StatusBadge>;
  }
  // No dates at all is NOT "out of warranty" — it's unknown, and saying otherwise invents
  // a fact that could cost the shop a covered claim.
  return <StatusBadge tone="neutral">Warranty not recorded</StatusBadge>;
}

/** "14 years old" — the sentence that starts a replacement conversation. */
export function AgeLabel({
  installedOn,
  today = todayIso(),
}: {
  installedOn: string | null;
  today?: string;
}) {
  const years = ageInYears(today, installedOn);
  if (years === null)
    return <span className="text-muted-foreground">Install date unknown</span>;
  if (years === 0)
    return <span className="text-muted-foreground">Installed this year</span>;
  return (
    <span
      className={years >= 12 ? 'text-[var(--sw-warn-text)]' : 'text-muted-foreground'}
    >
      {years} year{years === 1 ? '' : 's'} old
    </span>
  );
}
